import { Form, Radio, Flex, Select, ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { usePermissions } from 'business/contextProviders/usePermissions';
import {
  useReportActivityAnalyzeFilters,
  useReportActivityAnalyzeFiltersFormName,
} from 'business/shift-report/report-analyze/pages/activity-analyze/providers/filters';
import { ActivityAnalyzeRangeFilters } from 'business/shift-report/report-analyze/pages/activity-analyze/providers/filters/types';
import {
  ACTIVITY_AGGREGATION_LEVELS,
  ACTIVITY_CRITICAL_TYPES,
  ACTIVITY_DISPLAY_TYPES,
  ACTIVITY_DISPLAY_TYPES_MAP,
  ACTIVITY_GROUP_BY_TYPES,
  ACTIVITY_GROUP_BY_TYPES_MAP,
} from 'business/shift-report/report-analyze/pages/activity-analyze/types';
import {
  Module_Enum,
  useGetCurrentRingShiftReportQuery,
} from 'generated/graphql';
import NumberRangeInput from 'ui/form/number-range-input';
import TemporalitySelectInput, {
  DateSelectionTypeEnum,
} from 'ui/form/temporality-select-input';

type ReportActivityAnalyzeFiltersProps = {
  noDefaultFor?: DateSelectionTypeEnum[];
};

const ReportActivityAnalyzeFilters = ({
  noDefaultFor,
}: ReportActivityAnalyzeFiltersProps) => {
  const { t } = useTranslation();
  const { constructionSiteId } = usePermissions();
  const [form] = Form.useForm<ActivityAnalyzeRangeFilters>();
  const formName = useReportActivityAnalyzeFiltersFormName();
  const filters = useReportActivityAnalyzeFilters();
  const gqlHeaders = useGraphqlApiHeaders(Module_Enum.ShiftReport);

  const { data, error } = useGetCurrentRingShiftReportQuery({
    variables: { constructionSiteId },
    context: {
      headers: gqlHeaders,
    },
  });

  return (
    <ConfigProvider
      theme={{
        components: {
          Form: {
            inlineItemMarginBottom: 12,
          },
        },
      }}
    >
      <Form form={form} name={formName} initialValues={filters} layout="inline">
        <Form.Item
          name="filterType"
          label={t('input.filterByOptions.label')}
          layout="vertical"
        >
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            options={[
              {
                label: t('input.filterByOptions.date'),
                value: 'date',
              },
              {
                label: t('input.filterByOptions.ring'),
                value: 'ring',
              },
            ]}
          />
        </Form.Item>
        <Form.Item
          shouldUpdate={(prevValues, curValues) =>
            prevValues.filterType !== curValues.filterType
          }
          noStyle
        >
          {({ getFieldValue }) => {
            const filterType = getFieldValue('filterType');

            if (filterType === 'date') {
              return (
                <Flex>
                  <Form.Item
                    name="dateRange"
                    label={t('input.dateRange.label')}
                    layout="vertical"
                  >
                    <TemporalitySelectInput
                      defaultDate={dayjs()}
                      noDefaultFor={noDefaultFor}
                    />
                  </Form.Item>
                </Flex>
              );
            } else if (filterType === 'ring') {
              return (
                <Form.Item
                  name="ringRange"
                  label={t('input.ringRange.label')}
                  layout="vertical"
                >
                  <NumberRangeInput
                    leftInputProps={{
                      min: 0,
                      placeholder: t('input.ringRange.startPlaceholder'),
                    }}
                    rightInputProps={{
                      min: 0,
                      placeholder: t('input.ringRange.endPlaceholder'),
                    }}
                    bottomMessage={
                      error
                        ? t('dataAnalysis.errors.currentRingNumberError')
                        : t('dataAnalysis.labels.currentRingNumber', {
                            ringNumber: data?.task.at(0)?.ring,
                          })
                    }
                    allowClear
                  />
                </Form.Item>
              );
            }
            return null;
          }}
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(currentValues, nextValues) =>
            currentValues.displayType !== nextValues.displayType
          }
        >
          {({ getFieldValue }) => {
            const displayTypeCurrentValue = getFieldValue('displayType');
            return (
              <Form.Item
                name="groupByType"
                label={t('reportAnalyze.activityAnalyze.input.groupByOptions')}
                style={{ minWidth: '110px' }}
                layout="vertical"
              >
                <Select
                  disabled={
                    displayTypeCurrentValue === ACTIVITY_DISPLAY_TYPES_MAP.pie
                  }
                  popupMatchSelectWidth={false}
                  options={ACTIVITY_GROUP_BY_TYPES.filter(
                    (key) => key !== ACTIVITY_GROUP_BY_TYPES_MAP.activity,
                  ).map((value) => ({
                    label: t(
                      `reportAnalyze.activityAnalyze.input.groupByOptions`,
                      {
                        context: value,
                      },
                    ),
                    value,
                  }))}
                />
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item
          name="aggregationLevel"
          label={t('reportAnalyze.activityAnalyze.input.aggregationOptions')}
          layout="vertical"
        >
          <Select
            popupMatchSelectWidth={false}
            options={ACTIVITY_AGGREGATION_LEVELS.map((value) => ({
              label: t(
                `reportAnalyze.activityAnalyze.input.aggregationOptionsLevel`,
                {
                  level: value,
                },
              ),
              value,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="criticalType"
          label={t('reportAnalyze.activityAnalyze.input.criticalTypeOptions')}
          style={{ minWidth: '200px' }}
          layout="vertical"
        >
          <Select
            popupMatchSelectWidth={false}
            options={ACTIVITY_CRITICAL_TYPES.map((value) => ({
              label: t(
                `reportAnalyze.activityAnalyze.input.criticalTypeOptions`,
                {
                  context: value,
                },
              ),
              value,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="vChartScale"
          label={t('reportAnalyze.activityAnalyze.input.vChartRange')}
          layout="vertical"
        >
          <NumberRangeInput
            leftInputProps={{
              placeholder: t(
                'reportAnalyze.activityAnalyze.input.vChartRange',
                { context: 'minPlaceholder' },
              ),
            }}
            rightInputProps={{
              placeholder: t(
                'reportAnalyze.activityAnalyze.input.vChartRange',
                { context: 'maxPlaceholder' },
              ),
            }}
            allowClear
          />
        </Form.Item>
        <Form.Item
          name="displayType"
          label={t('reportAnalyze.activityAnalyze.input.displayTypeOptions')}
          layout="vertical"
        >
          <Select
            popupMatchSelectWidth={false}
            options={ACTIVITY_DISPLAY_TYPES.map((value) => ({
              label: t(
                `reportAnalyze.activityAnalyze.input.displayTypeOptions`,
                {
                  context: value,
                },
              ),
              value,
            }))}
          />
        </Form.Item>
      </Form>
    </ConfigProvider>
  );
};

export default ReportActivityAnalyzeFilters;
